/**
 * Mes examens à venir — vue formateur.
 * Affiche les créneaux d'examens PUBLIÉS où le formateur intervient, groupés
 * par session. Chaque créneau montre la date, l'heure, la matière et le
 * groupe d'options, avec un badge « Aujourd'hui / Demain / Dans N jours ».
 */
import { useMemo } from 'react';
import {
  Calendar, Loader2, Clock, GraduationCap, AlertCircle, BookOpen, Users, Bell,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import { useMesExamensAVenirQuery } from '../../services/formateurApi';

const JOURS_LABEL: Record<string, string> = {
  Monday: 'Lundi', Tuesday: 'Mardi', Wednesday: 'Mercredi',
  Thursday: 'Jeudi', Friday: 'Vendredi', Saturday: 'Samedi', Sunday: 'Dimanche',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

function badgeJoursAvant(j: number): { label: string; classes: string } {
  if (j === 0) return { label: "Aujourd'hui", classes: 'bg-red-500/20 text-red-300 border-red-500/50' };
  if (j === 1) return { label: 'Demain', classes: 'bg-orange-500/20 text-orange-300 border-orange-500/50' };
  if (j <= 3) return { label: `Dans ${j} jours`, classes: 'bg-amber-500/20 text-amber-300 border-amber-500/50' };
  if (j <= 7) return { label: `Dans ${j} jours`, classes: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40' };
  return { label: `Dans ${j} jours`, classes: 'bg-blue-500/15 text-blue-300 border-blue-500/40' };
}

export default function MesExamens() {
  const { data, isLoading, error, refetch } = useMesExamensAVenirQuery();

  // Groupé par date, puis par session
  const parJour = useMemo(() => {
    if (!data?.results) return [];
    const map = new Map<string, typeof data.results>();
    data.results.forEach((c) => {
      const arr = map.get(c.date) || [];
      arr.push(c);
      map.set(c.date, arr);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <FormateurLayout>
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        <header className="flex items-center gap-3">
          <Calendar size={22} className="text-amber-400" />
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">Mes examens à venir</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              EDT publiés où vous intervenez comme correcteur / surveillant
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="text-xs px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg"
          >
            Actualiser
          </button>
        </header>

        {isLoading && (
          <div className="text-center py-10 text-gray-500">
            <Loader2 size={20} className="inline animate-spin mr-2" />
            Chargement…
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-300 p-3 rounded-lg text-sm">
            Impossible de charger vos examens.
          </div>
        )}

        {!isLoading && data && data.total === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
            <AlertCircle size={28} className="mx-auto text-gray-500 mb-2" />
            <p className="text-sm text-gray-300">
              Aucun examen publié à venir pour vous.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Cette liste s'active quand la scolarité publie une session d'examens
              qui inclut vos matières.
            </p>
          </div>
        )}

        {/* Sessions concernées (résumé) */}
        {data && data.sessions.length > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Sessions concernées
            </div>
            <div className="flex flex-wrap gap-2">
              {data.sessions.map((s) => (
                <div key={s.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs">
                  <BookOpen size={12} className="text-amber-400" />
                  <span className="font-medium text-gray-200">{s.nom}</span>
                  <span className="text-gray-500">·</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    s.type === 'rattrapage'
                      ? 'bg-orange-500/20 text-orange-300'
                      : 'bg-emerald-500/20 text-emerald-300'
                  }`}>
                    {s.type === 'rattrapage' ? 'Rattrapage' : 'Ordinaire'}
                  </span>
                  <span className="text-gray-500">·</span>
                  <span className="text-gray-400">{s.nb_creneaux_moi} créneau(x)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liste par jour */}
        {parJour.map(([dateIso, creneaux]) => {
          const jours_avant = creneaux[0].jours_avant;
          const badge = badgeJoursAvant(jours_avant);
          return (
            <section key={dateIso} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <header className="px-4 py-2.5 bg-gray-800/60 border-b border-gray-800 flex items-center gap-3">
                <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-full ${badge.classes}`}>
                  {badge.label}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white capitalize">
                    {JOURS_LABEL[creneaux[0].jour_semaine] || creneaux[0].jour_semaine} · {fmtDate(dateIso)}
                  </div>
                </div>
                <span className="text-[10px] text-gray-500">
                  {creneaux.length} matière{creneaux.length > 1 ? 's' : ''}
                </span>
              </header>
              <ul className="divide-y divide-gray-800">
                {creneaux.map((c) => (
                  <li key={c.creneau_id} className="p-3 flex items-start gap-3">
                    <div className="w-20 flex-shrink-0 text-center">
                      <Clock size={14} className="inline text-gray-500" />
                      <div className="text-xs font-medium text-gray-200 mt-0.5">
                        {c.heure_debut}
                      </div>
                      <div className="text-[10px] text-gray-500">→ {c.heure_fin}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">
                        {c.matiere}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-400 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <GraduationCap size={11} /> {c.niveau}
                        </span>
                        {c.groupe_label && (
                          <span className="inline-flex items-center gap-1">
                            <Users size={11} /> {c.groupe_label}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-semibold ${
                          c.session_type === 'rattrapage'
                            ? 'bg-orange-500/15 text-orange-300'
                            : 'bg-emerald-500/15 text-emerald-300'
                        }`}>
                          S{c.session_semestre} · {c.session_type === 'rattrapage' ? 'Rattrapage' : 'Ordinaire'}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {data && data.total > 0 && (
          <div className="text-[11px] text-gray-500 text-center py-2 flex items-center justify-center gap-2">
            <Bell size={12} />
            Vous recevez un rappel push chaque matin pour vos examens à venir.
          </div>
        )}
      </div>
    </FormateurLayout>
  );
}
